import { MediaItemContent } from "@bandwith/ui";
import { Plus } from "lucide-react";
import { useMemo } from "react";

type SongListItem = {
  id: string;
  title: string;
  author?: { name?: string | null } | null;
};

type Props = {
  songs: SongListItem[];
  selectedSongIds: string[];
  onAddSong: (songId: string) => void;
  disabled?: boolean;
  emptyMessage?: string;
};

export function SongPicker({
  songs,
  selectedSongIds,
  onAddSong,
  disabled = false,
  emptyMessage = "No songs available. Create some songs first!",
}: Props) {
  const selectedSet = useMemo(
    () => new Set(selectedSongIds),
    [selectedSongIds],
  );
  const availableSongs = useMemo(
    () => songs.filter((song) => !selectedSet.has(song.id)),
    [songs, selectedSet],
  );

  if (songs.length === 0) {
    return <p className="text-text-muted">{emptyMessage}</p>;
  }

  if (availableSongs.length === 0) {
    return <p className="text-text-muted">All songs have been added.</p>;
  }

  return (
    <ul className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-border-subtle bg-bg-secondary p-2">
      {availableSongs.map((song) => (
        <li key={song.id}>
          <button
            type="button"
            onClick={() => onAddSong(song.id)}
            disabled={disabled}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-bg-tertiary disabled:opacity-50"
          >
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-border-subtle text-text-muted">
              <Plus className="h-4 w-4" />
            </span>
            <MediaItemContent title={song.title} subtitle={song.author?.name} />
          </button>
        </li>
      ))}
    </ul>
  );
}
